/**
 * QuestionnaireLauncher
 *
 * Business logic wrapper that uses Artifact component for rendering.
 * Manages the lifecycle of the questionnaire (create orchestrator, open panel, handle completion).
 *
 * States:
 * - ready: Initial state, shows "Begin Questionnaire"
 * - open: Panel is open with questionnaire, shows "Questionnaire Open"
 * - paused: Panel closed before completion, shows "Resume Questionnaire"
 * - completed: Questionnaire submitted successfully, shows "Completed"
 */

import { BatchQuestionnaireOrchestrator } from './BatchQuestionnaireOrchestrator.js';
import { Artifact } from './Artifact.js';
import { API } from '../core/api.js';

export class QuestionnaireLauncher {
  /**
   * @param {Object} options - Configuration options
   * @param {Object} options.questionnaireData - Data from API (questionnaire, questions, displayRules)
   * @param {Object} [options.pathMetadata] - Path context (id, name, phase, etc.)
   * @param {EventBus} options.eventBus - Event bus for communication
   * @param {Function} options.onSubmit - Callback for questionnaire submission
   * @param {Function} options.onComplete - Callback for questionnaire completion
   * @param {Function} options.onStateChange - Callback when launcher state changes
   */
  constructor(options) {
    this.questionnaireData = options.questionnaireData;
    this.pathMetadata = options.pathMetadata || {};
    this.eventBus = options.eventBus;
    this.onSubmitCallback = options.onSubmit;
    this.onCompleteCallback = options.onComplete;
    this.onStateChangeCallback = options.onStateChange;

    // Extract questionnaire info for display
    this.questionnaire = this.questionnaireData.questionnaire;
    this.questionnaireId = this.questionnaire.id;
    this.questionnaireName = this.questionnaire.name || 'Questionnaire';
    this.questionnaireDescription = this.questionnaire.description || '';
    this.estimatedMinutes = this.questionnaire.estimatedMinutes || null;
    this.questionCount = this.questionnaireData.questionCount || 0;

    // State
    this.state = 'ready'; // ready | open | paused | completed | skipped
    this.orchestrator = null;
    this.orchestratorContainer = null;

    // Artifact component reference
    this.artifact = null;

    // Bound event handlers for cleanup
    this._boundHandlePanelClosed = this._handlePanelClosed.bind(this);
    this._boundHandleQuestionnaireCompleted = this._handleQuestionnaireCompleted.bind(this);

    // Set up event listeners
    this._setupEventListeners();
  }

  /**
   * Get state-specific configuration for the artifact
   * @private
   * @returns {Object} Artifact configuration for current state
   */
  _getStateConfig() {
    const baseSubtitle = this._buildSubtitle();

    const configs = {
      ready: {
        title: this.questionnaireName,
        description: this.questionnaireDescription,
        subtitle: baseSubtitle,
        showButton: false,
        showChevron: true,
        chevronText: '',
        showCheckmark: false,
        showIcon: true,
        clickable: true
      },
      open: {
        title: this.questionnaireName,
        description: this.questionnaireDescription,
        subtitle: 'Questionnaire in progress...',
        showButton: false,
        showChevron: false,
        chevronText: '',
        showCheckmark: false,
        showIcon: true,
        clickable: false
      },
      paused: {
        title: this.questionnaireName,
        description: this.questionnaireDescription,
        subtitle: this._buildPausedSubtitle(),
        showButton: false,
        showChevron: true,
        chevronText: 'Resume',
        showCheckmark: false,
        showIcon: true,
        clickable: true
      },
      completed: {
        title: this.questionnaireName,
        description: '',
        subtitle: 'Completed',
        showButton: false,
        showChevron: false,
        chevronText: '',
        showCheckmark: true,
        showIcon: true,
        clickable: false
      },
      skipped: {
        title: this.questionnaireName,
        description: this.questionnaireDescription,
        subtitle: 'Skipped',
        showButton: false,
        showChevron: false,
        chevronText: '',
        showCheckmark: false,
        showIcon: true,
        clickable: false
      }
    };

    return configs[this.state] || configs.ready;
  }

  /**
   * Build subtitle text for ready state
   * @private
   * @returns {string} Subtitle text
   */
  _buildSubtitle() {
    const parts = [];

    // Add position indicator if path context is available.
    if (this.pathMetadata?.position && this.pathMetadata?.totalQuestionnaires) {
      parts.push(`Questionnaire ${this.pathMetadata.position} of ${this.pathMetadata.totalQuestionnaires}`);
    }

    if (this.questionCount > 0) {
      parts.push(`${this.questionCount} question${this.questionCount !== 1 ? 's' : ''}`);
    }
    if (this.estimatedMinutes) {
      parts.push(`~${this.estimatedMinutes} min`);
    }
    return parts.join(' · ') || 'Questionnaire';
  }

  /**
   * Build subtitle text for paused state
   * @private
   * @returns {string} Subtitle text with progress
   */
  _buildPausedSubtitle() {
    const progress = this._getProgress();
    return `${progress.answered} of ${progress.total} answered`;
  }

  /**
   * Render the launcher
   * @returns {HTMLElement} The artifact element
   */
  render() {
    const config = this._getStateConfig();

    this.artifact = new Artifact({
      iconId: 'icon-questionnaire',
      title: config.title,
      description: config.description,
      subtitle: config.subtitle,
      showButton: config.showButton,
      showChevron: config.showChevron,
      chevronText: config.chevronText,
      showCheckmark: config.showCheckmark,
      showIcon: config.showIcon,
      state: this.state,
      type: 'questionnaire',
      clickable: config.clickable,
      onArtifactClick: () => this._handleClick(),
      onButtonClick: () => this._handleClick()
    });

    const element = this.artifact.render();
    element.dataset.questionnaireId = this.questionnaireId;

    return element;
  }

  /**
   * Handle click on artifact or button
   * @private
   */
  _handleClick() {
    if (this.state === 'ready' || this.state === 'paused') {
      // Log the open/resume action (fire-and-forget, don't await)
      this._logQuestionnaireAction('opened', {
        wasResumed: this.state === 'paused'
      });
      this.openPanel();
    }
  }

  /**
   * Update artifact display based on current state
   * @private
   * @param {Object} [options] - Render options forwarded to Artifact.update
   */
  _updateArtifact(options = {}) {
    if (!this.artifact) return;

    const config = this._getStateConfig();

    this.artifact.update({
      title: config.title,
      description: config.description,
      subtitle: config.subtitle,
      showButton: config.showButton,
      showChevron: config.showChevron,
      chevronText: config.chevronText,
      showCheckmark: config.showCheckmark,
      showIcon: config.showIcon,
      state: this.state,
      clickable: config.clickable
    }, options);
  }

  /**
   * Set up event listeners for panel and questionnaire events
   * @private
   */
  _setupEventListeners() {
    if (!this.eventBus) return;

    this.eventBus.on('right-panel:closed', this._boundHandlePanelClosed);
    this.eventBus.on('questionnaire:completed', this._boundHandleQuestionnaireCompleted);
  }

  /**
   * Handle panel closed event
   * @private
   * @param {Object} data - Event data with reason
   */
  _handlePanelClosed(data = {}) {
    console.log('[DEBUG QuestionnaireLauncher] Panel closed handler called, state:', this.state, 'reason:', data.reason);
    // Only react if this launcher's questionnaire is open
    if (this.state === 'open') {
      this.setState('paused');

      // Only emit questionnaire:closed for user-initiated closes
      // Navigation-triggered closes should not generate system messages
      if (data.reason !== 'navigation' && this.eventBus) {
        const progress = this._getProgress();

        // Log the close/abandon action (fire-and-forget)
        this._logQuestionnaireAction('closed', {
          progress,
          answeredQuestions: progress.answered,
          totalQuestions: progress.total
        });

        console.log('[DEBUG QuestionnaireLauncher] Emitting questionnaire:closed event', {
          questionnaireId: this.questionnaireId,
          progress
        });
        this.eventBus.emit('questionnaire:closed', {
          questionnaireId: this.questionnaireId,
          pathId: this.pathMetadata.id,
          pathMetadata: this.pathMetadata,
          progress
        });
      } else {
        console.log('[DEBUG QuestionnaireLauncher] NOT emitting - reason:', data.reason, 'eventBus:', !!this.eventBus);
      }
    } else {
      console.log('[DEBUG QuestionnaireLauncher] NOT handling - state is:', this.state);
    }
  }

  /**
   * Handle questionnaire completed event (fallback)
   * Primary completion is handled in _createOrchestrator's onComplete callback
   * This acts as a fallback in case the direct callback doesn't fire
   * @private
   * @param {Object} data - Completion data
   */
  _handleQuestionnaireCompleted(data) {
    // Only react if this launcher's questionnaire completed AND not already completed
    if (data.questionnaireId === this.questionnaireId && this.state !== 'completed') {
      this.setState('completed');
      // Panel close already handled by onComplete callback or will be handled there
    }
  }

  /**
   * Open the side panel with the questionnaire
   */
  openPanel() {
    // Create orchestrator if not exists
    if (!this.orchestrator) {
      this._createOrchestrator();
    }

    // Open right panel with questionnaire content
    if (this.eventBus) {
      // The wizard's question-number pill takes the header title slot in place of the
      // questionnaire name (which is already on the launcher card in the transcript).
      // It hides itself on the review step, leaving the header empty by design.
      this.eventBus.emit('right-panel:open', {
        titleNode: this.orchestrator.questionNumberEl,
        titleAlign: 'center',
        content: this.orchestratorContainer,
        contentType: 'questionnaire'
      });

      this.eventBus.emit('questionnaire:opened', {
        questionnaireId: this.questionnaireId,
        questionnaireName: this.questionnaireName,
        estimatedMinutes: this.estimatedMinutes
      });
    }

    this.setState('open');

    // Initialize orchestrator after panel opens (DOM attached)
    // Use requestAnimationFrame to ensure panel transition has started
    requestAnimationFrame(() => {
      if (this.orchestrator && !this.orchestrator._initialized) {
        this.orchestrator.initialize();
        this.orchestrator._initialized = true;
      }
    });
  }

  /**
   * Create the BatchQuestionnaireOrchestrator instance
   * @private
   */
  _createOrchestrator() {
    // Create container
    this.orchestratorContainer = document.createElement('div');
    this.orchestratorContainer.className = 'panel-questionnaire-container';

    // Create orchestrator with panel-specific options
    this.orchestrator = new BatchQuestionnaireOrchestrator(
      this.orchestratorContainer,
      this.questionnaireData,
      {
        onSubmit: async (responses, deactivatedQuestionIds) => {
          if (this.onSubmitCallback) {
            return await this.onSubmitCallback(responses, deactivatedQuestionIds);
          }
        },
        onComplete: async (completionData) => {
          // Set state to completed IMMEDIATELY (before callback chain)
          this.setState('completed');

          // Log the completion action (fire-and-forget)
          this._logQuestionnaireAction('completed', {
            totalQuestions: completionData.responses?.size || 0
          });

          // Close the panel
          if (this.eventBus) {
            this.eventBus.emit('right-panel:close');
          }

          // Call the callback (which will emit questionnaire:completed for DiscoveryMode)
          if (this.onCompleteCallback) {
            return await this.onCompleteCallback(completionData);
          }
        },
        pillInHeader: true, // Question-number pill is mounted in the panel header (see openPanel)
        pathMetadata: this.pathMetadata,
        eventBus: this.eventBus
      }
    );

    // Render orchestrator (but don't initialize until panel opens)
    this.orchestrator.render();
  }

  /**
   * Get current progress info
   * @private
   * @returns {Object} Progress info (answered, total)
   */
  _getProgress() {
    if (!this.orchestrator) {
      return { answered: 0, total: this.questionnaireData.questions?.length || 0 };
    }

    const total = this.orchestrator.visibleQuestions?.length || 0;
    let answered = 0;

    if (this.orchestrator.questionRenderers) {
      for (const [, renderer] of this.orchestrator.questionRenderers) {
        if (renderer.getResponse() !== null) {
          answered++;
        }
      }
    }

    return { answered, total };
  }

  /**
   * Log questionnaire action to the database (silent audit logging)
   * These messages are stored for session history but not displayed in chat
   * @private
   * @param {string} action - Action type: 'opened', 'completed', 'closed'
   * @param {Object} [additionalMetadata={}] - Extra metadata to include
   */
  async _logQuestionnaireAction(action, additionalMetadata = {}) {
    const sessionId = window.sessionManager?.getCurrentSessionId();
    if (!sessionId) {
      console.warn('[QuestionnaireLauncher] No session ID, skipping action logging');
      return;
    }

    // Build human-readable content based on action
    const actionLabels = {
      opened: `[Opened questionnaire: ${this.questionnaireName}]`,
      completed: `[Completed questionnaire: ${this.questionnaireName}]`,
      closed: `[Closed questionnaire: ${this.questionnaireName} (incomplete)]`
    };

    const content = actionLabels[action] || `[Questionnaire action: ${action}]`;

    try {
      await API.recordButtonInteraction(
        sessionId,
        content,
        `questionnaire:${action}`,
        null, // No button group
        {
          type: 'questionnaire_action',
          action,
          questionnaireId: this.questionnaireId,
          questionnaireName: this.questionnaireName,
          pathId: this.pathMetadata?.id,
          pathName: this.pathMetadata?.name,
          hidden: true, // Mark as hidden for potential future filtering
          ...additionalMetadata
        }
      );

      console.log(`[QuestionnaireLauncher] Logged action: ${action} for ${this.questionnaireName}`);
    } catch (error) {
      // Don't block user interaction if logging fails
      console.error('[QuestionnaireLauncher] Failed to log action:', error);
    }
  }

  /**
   * Set launcher state
   * @param {string} newState - New state (ready, open, paused, completed)
   */
  setState(newState) {
    const oldState = this.state;
    this.state = newState;

    // Completion drops the description line, so the card gets shorter. Ease it —
    // this fires while the panel is still sliding out, and an instant reflow
    // there hops the whole transcript below the card.
    this._updateArtifact({ animateHeight: newState === 'completed' && oldState !== 'completed' });

    // Manage input overlay based on state transitions
    if (newState === 'open' && oldState !== 'open') {
      this._setInputDisabled(true);
    } else if (oldState === 'open' && newState !== 'open') {
      this._setInputDisabled(false);
    }

    if (this.onStateChangeCallback) {
      this.onStateChangeCallback(newState, oldState);
    }

    console.log(`[QuestionnaireLauncher] State: ${oldState} → ${newState}`);
  }

  /**
   * Toggle chat input disabled state based on questionnaire state
   * @private
   * @param {boolean} disabled - Whether to disable input
   */
  _setInputDisabled(disabled) {
    if (!this.eventBus) return;
    if (disabled) {
      this.eventBus.emit('input:disabled', {
        title: 'Questionnaire in Progress',
        subtitle: 'Close the panel to resume your conversation'
      });
    } else {
      this.eventBus.emit('input:enabled');
    }
  }

  /**
   * Get current state
   * @returns {string} Current state
   */
  getState() {
    return this.state;
  }

  /**
   * Get questionnaire ID
   * @returns {string} Questionnaire ID
   */
  getQuestionnaireId() {
    return this.questionnaireId;
  }

  /**
   * Cleanup and destroy
   */
  destroy() {
    // Remove event listeners
    if (this.eventBus) {
      this.eventBus.off('right-panel:closed', this._boundHandlePanelClosed);
      this.eventBus.off('questionnaire:completed', this._boundHandleQuestionnaireCompleted);
    }

    // Destroy orchestrator if exists
    if (this.orchestrator && typeof this.orchestrator.destroy === 'function') {
      this.orchestrator.destroy({ clearDraft: false }); // Preserve draft on launcher destroy
    }

    // Destroy artifact
    if (this.artifact) {
      this.artifact.destroy();
      this.artifact = null;
    }

    // Clear references
    this.orchestrator = null;
    this.orchestratorContainer = null;
  }
}
